import { useCallback, useState } from "react";

import { apiClient } from "../api/client";
import { toast } from "../stores/toastStore";

/**
 * Downloading a file from an authenticated API (FS-023).
 *
 * Six places in this app linked to an export with a bare `<a href>`. None of
 * them worked. The access token lives in `localStorage` and is attached by the
 * axios request interceptor — a browser navigation carries no such header, so
 * every one of those links returned 401, and the user got either a login
 * redirect or a downloaded JSON error with a `.csv` extension.
 *
 * So a download has to be a request like any other: fetch it as a blob through
 * the same client, then hand the bytes to the browser. The filename comes from
 * the server's `Content-Disposition`, because the server is the thing that knows
 * whether it just produced a CSV or an XLSX.
 */

/** Pulls the filename out of a `Content-Disposition` header, if it set one. */
function filenameFrom(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  // RFC 5987 form first — it carries the encoding, so it wins when present.
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header is not worth failing a download over.
    }
  }
  const quoted = /filename="?([^";]+)"?/i.exec(header);
  return quoted?.[1]?.trim() || fallback;
}

export interface DownloadOptions {
  /** Query parameters, e.g. `{ format: "xlsx", year: 2026 }`. */
  params?: Record<string, string | number | boolean | undefined>;
  /** Used only when the server sends no `Content-Disposition`. */
  fallbackFilename?: string;
  /** Shown on success. Omitted means no toast, which suits a quiet export button. */
  successMessage?: string;
}

export function useDownload() {
  const [isDownloading, setIsDownloading] = useState(false);
  /** The URL currently in flight, so one button can spin without the others. */
  const [activeUrl, setActiveUrl] = useState<string | null>(null);

  const download = useCallback(
    async (url: string, options: DownloadOptions = {}): Promise<boolean> => {
      const { params, fallbackFilename = "download", successMessage } = options;
      setIsDownloading(true);
      setActiveUrl(url);
      try {
        const response = await apiClient.get(url, {
          params,
          responseType: "blob",
        });
        const filename = filenameFrom(
          response.headers?.["content-disposition"] as string | undefined,
          fallbackFilename,
        );

        const objectUrl = window.URL.createObjectURL(response.data as Blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Revoked on the next tick: Safari has not finished reading the blob
        // when click() returns, and revoking synchronously gives an empty file.
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);

        if (successMessage) toast.success(successMessage, filename);
        return true;
      } catch (error: unknown) {
        // An error body arrives as a Blob because we asked for one, so the
        // server's message has to be read back out before it can be shown.
        // Without this the user sees "Request failed with status code 400"
        // instead of "format must be one of csv, xlsx".
        let detail = "The download could not be completed.";
        const response = (
          error as { response?: { data?: unknown; status?: number } }
        )?.response;
        if (response?.data instanceof Blob) {
          try {
            const text = await response.data.text();
            const parsed = text.trim().startsWith("{")
              ? JSON.parse(text)
              : null;
            detail = parsed?.detail || text || detail;
          } catch {
            // Keep the generic message.
          }
        } else if (typeof response?.data === "string") {
          detail = response.data;
        }
        if (response?.status === 403) {
          detail = "You do not have permission to export this.";
        }
        toast.error("Export failed", detail);
        return false;
      } finally {
        setIsDownloading(false);
        setActiveUrl(null);
      }
    },
    [],
  );

  return { download, isDownloading, activeUrl };
}
